import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from '../environments/environment';
import {
  ChatMessage,
  ConversationListResponse,
  ConversationSummary,
  CreateNotificationPayload,
  CreateConversationRequest,
  MessagesListResponse,
  MarkConversationReadResponse,
  NotificationListResponse,
  NotificationRecord,
  RemoveParticipantResponse,
  SendMessageRequest,
  UnreadSummaryResponse,
} from '../chat/chat.types';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private readonly baseUrl = (environment.chatServiceUrl || '').replace(/\/$/, '');
  private readonly unreadRefresh$ = new Subject<void>();

  constructor(private readonly http: HttpClient) {}

  listConversations(
    userId: string,
    params: { limit?: number; cursor?: string | null; search?: string | null } = {},
  ): Observable<ConversationListResponse> {
    let httpParams = new HttpParams().set('userId', userId);

    if (params.limit) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }
    if (params.cursor) {
      httpParams = httpParams.set('cursor', params.cursor);
    }
    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }

    return this.http.get<ConversationListResponse>(`${this.baseUrl}/conversations`, {
      params: httpParams,
      headers: this.buildHeaders(),
    });
  }

  getUnreadSummary(userId: string): Observable<UnreadSummaryResponse> {
    const params = new HttpParams().set('userId', userId);
    return this.http.get<UnreadSummaryResponse>(
      `${this.baseUrl}/conversations/unread/summary`,
      { params, headers: this.buildHeaders() },
    );
  }

  onUnreadRefresh(): Observable<void> {
    return this.unreadRefresh$.asObservable();
  }

  requestUnreadRefresh(): void {
    this.unreadRefresh$.next();
  }

  createConversation(payload: CreateConversationRequest): Observable<ConversationSummary> {
    return this.http.post<ConversationSummary>(`${this.baseUrl}/conversations`, payload, {
      headers: this.buildHeaders(),
    });
  }

  getConversation(conversationId: string, userId: string): Observable<ConversationSummary> {
    const params = new HttpParams().set('userId', userId);
    return this.http.get<ConversationSummary>(`${this.baseUrl}/conversations/${conversationId}`, {
      params,
      headers: this.buildHeaders(),
    });
  }

  listMessages(
    conversationId: string,
    params: { userId: string; limit?: number; cursor?: string | null },
  ): Observable<MessagesListResponse> {
    let httpParams = new HttpParams()
      .set('userId', params.userId)
      .set('limit', (params.limit ?? 25).toString());

    if (params.cursor) {
      httpParams = httpParams.set('cursor', params.cursor);
    }

    return this.http.get<MessagesListResponse>(
      `${this.baseUrl}/conversations/${conversationId}/messages`,
      { params: httpParams, headers: this.buildHeaders() },
    );
  }

  sendMessage(
    conversationId: string,
    payload: SendMessageRequest,
  ): Observable<ChatMessage> {
    return this.http.post<ChatMessage>(
      `${this.baseUrl}/conversations/${conversationId}/messages`,
      payload,
      { headers: this.buildHeaders() },
    );
  }

  sendChatPush(payload: {
    user_ids: string[];
    title: string;
    message: string;
    data?: Record<string, any>;
  }): Observable<any> {
    const apiBaseUrl = this.resolveApiBaseForPush();
    return this.http.post<any>(`${apiBaseUrl}/users/send-chat-push`, payload, {
      headers: this.buildHeaders(),
    });
  }

  private resolveApiBaseForPush(): string {
    const configuredBaseUrl = (environment.baseUrl || '').replace(/\/$/, '');
    console.log('configuredBaseUrl: ', configuredBaseUrl);

    if (typeof window === 'undefined') {
      return configuredBaseUrl;
    }

    const hostname = window.location.hostname || '';
    const isDevSite = hostname === 'dev-app.knockvia.com';
    if (isDevSite && !configuredBaseUrl.includes('dev-api.knockvia.com')) {
      return 'https://dev-api.knockvia.com';
    }

    return configuredBaseUrl;
  }

  markConversationRead(
    conversationId: string,
    payload: { userId: string; lastReadAt?: string | null },
  ): Observable<MarkConversationReadResponse> {
    return this.http.post<MarkConversationReadResponse>(
      `${this.baseUrl}/conversations/${conversationId}/read`,
      payload,
      { headers: this.buildHeaders() },
    );
  }

  listNotifications(
    userId: string,
    params: { limit?: number; cursor?: string | null; onlyUnread?: boolean } = {},
  ): Observable<NotificationListResponse> {
    let httpParams = new HttpParams().set('userId', userId);

    if (params.limit) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }
    if (params.cursor) {
      httpParams = httpParams.set('cursor', params.cursor);
    }
    if (params.onlyUnread !== undefined) {
      httpParams = httpParams.set('onlyUnread', String(params.onlyUnread));
    }

    return this.http.get<NotificationListResponse>(`${this.baseUrl}/notifications`, {
      params: httpParams,
      headers: this.buildHeaders(),
    });
  }

  markNotificationAsRead(
    notificationId: string,
    userId: string,
  ): Observable<NotificationRecord> {
    return this.http.post<NotificationRecord>(
      `${this.baseUrl}/notifications/${notificationId}/read`,
      { userId },
      { headers: this.buildHeaders() },
    );
  }

  createNotification(payload: CreateNotificationPayload): Observable<NotificationRecord> {
    return this.http.post<NotificationRecord>(`${this.baseUrl}/notifications`, payload, {
      headers: this.buildHeaders(),
    });
  }

  renameConversation(
    conversationId: string,
    payload: { userId: string; title?: string | null },
  ): Observable<ConversationSummary> {
    return this.http.patch<ConversationSummary>(
      `${this.baseUrl}/conversations/${conversationId}/title`,
      payload,
      { headers: this.buildHeaders() },
    );
  }

  addParticipant(
    conversationId: string,
    payload: { userId: string; participantId: string },
  ): Observable<ConversationSummary> {
    return this.http.post<ConversationSummary>(
      `${this.baseUrl}/conversations/${conversationId}/participants`,
      payload,
      { headers: this.buildHeaders() },
    );
  }

  removeParticipant(
    conversationId: string,
    participantId: string,
    userId: string,
  ): Observable<RemoveParticipantResponse> {
    const params = new HttpParams().set('userId', userId);
    return this.http.delete<RemoveParticipantResponse>(
      `${this.baseUrl}/conversations/${conversationId}/participants/${encodeURIComponent(
        participantId,
      )}`,
      { params, headers: this.buildHeaders() },
    );
  }

  listCompanyUsers(companyId: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/directory/companies/${encodeURIComponent(companyId)}/users`,
      { headers: this.buildHeaders() },
    );
  }

  listCompanyProviders(companyId: string): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/directory/providers/providers-by-company/${encodeURIComponent(
        companyId,
      )}`,
      { headers: this.buildHeaders() },
    );
  }

  private buildHeaders(): HttpHeaders {
    let headers = new HttpHeaders().set('Content-Type', 'application/json');
    const token = localStorage.getItem('access_token') || localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refresh_token') || localStorage.getItem('refreshToken');
    const rawUser = localStorage.getItem('user');
    let portalId = '';

    if (rawUser) {
      try {
        const user = JSON.parse(rawUser);
        portalId = user?.active_portal_id ? String(user.active_portal_id) : '';
      } catch (_) {
        portalId = '';
      }
    }

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    if (refreshToken) {
      headers = headers.set('Refresh', refreshToken);
    }
    if (portalId) {
      headers = headers.set('Portal-id', portalId);
    }
    return headers;
  }
}
